import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Table component container with responsive wrapper
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Table contents
 * @param {Object} [props.rest] - Additional HTML table attributes
 * @returns {JSX.Element} Table component
 */
const Table = ({ className, ...props }) => (
  <div className="">
    <table
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
);

/**
 * Table header component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Header contents
 * @param {Object} [props.rest] - Additional HTML thead attributes
 * @returns {JSX.Element} Table header section
 */
const TableHeader = ({ className, ...props }) => (
  <thead className={cn("[&_tr]:border-b", className)} {...props} />
);

/**
 * Table body component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Body contents
 * @param {Object} [props.rest] - Additional HTML tbody attributes
 * @returns {JSX.Element} Table body section
 */
const TableBody = ({ className, ...props }) => (
  <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />
);

/**
 * Table footer component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Footer contents
 * @param {Object} [props.rest] - Additional HTML tfoot attributes
 * @returns {JSX.Element} Table footer section
 */
const TableFooter = ({ className, ...props }) => (
  <tfoot
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
);

/**
 * Table row component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Row contents
 * @param {Object} [props.rest] - Additional HTML tr attributes
 * @returns {JSX.Element} Table row
 */
const TableRow = ({ className, ...props }) => (
  <tr
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
);

/**
 * Table header cell component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Cell contents
 * @param {Object} [props.rest] - Additional HTML th attributes
 * @returns {JSX.Element} Table header cell
 */
const TableHead = ({ className, ...props }) => (
  <th
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className
    )}
    {...props}
  />
);

/**
 * Table data cell component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Cell contents
 * @param {Object} [props.rest] - Additional HTML td attributes
 * @returns {JSX.Element} Table data cell
 */
const TableCell = ({ className, ...props }) => (
  <td className={cn("p-2 align-middle", className)} {...props} />
);

/**
 * Table caption component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional className string
 * @param {React.ReactNode} props.children - Caption contents
 * @param {Object} [props.rest] - Additional HTML caption attributes
 * @returns {JSX.Element} Table caption
 */
const TableCaption = ({ className, ...props }) => (
  <caption
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
);

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
